import React, { Component } from 'react';
import './mainpage.css';
import './bioland.css';
import NavBar from './NavBar';
import ticket1 from './assets/tickets/1.webp';
import ticket2 from './assets/tickets/2.webp';
import ticket3 from './assets/tickets/3.webp';
import ticket4 from './assets/tickets/4.webp';
import ticket5 from './assets/tickets/5.webp';
import ticket6 from './assets/tickets/6.webp';
import ticket7 from './assets/tickets/7.webp';

interface BiolandProps {}

interface BiolandState {
  lightboxSrc: string | null;
}

class Bioland extends Component<BiolandProps, BiolandState> {
  constructor(props: BiolandProps) {
    super(props);
    this.state = { lightboxSrc: null };
  }

  handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG' && target.closest('.bioland-figure')) {
      this.setState({ lightboxSrc: (target as HTMLImageElement).src });
    }
  };

  closeLightbox = () => {
    this.setState({ lightboxSrc: null });
  };

  render() {
    return (
      <div className="main-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="bioland-hero">
          <div className="container">
            <div className="bioland-hero-inner">
              <span className="bioland-label">Case Studies</span>
              <h1 className="bioland-hero-title">
                How Bioland Cyprus Transformed Request Management with pEntities Ticketing
              </h1>
              <div className="bioland-meta">
                <span>pEntities in Action</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="bioland-content-section">
          <div className="container">
            <div className="bioland-content" onClick={this.handleContentClick}>
              <p>
                At pEntities, our goal has always been simple: help organizations eliminate operational
                friction, increase visibility, and automate repetitive work using the tools they already
                use every day.
              </p>
              <p>
                One example of this impact comes from one of our customers, who adopted pEntities Ticketing
                to replace the traditional email-based request management process.
              </p>

              {/* Customer Feedback */}
              <h2>Customer Feedback</h2>
              <blockquote className="bioland-quote">
                <p>
                  "One of the biggest challenges we faced was managing requests through email. Many emails
                  were lost in the volume of daily communication or simply forgotten over time, leading to
                  delays, limited visibility, and difficulties tracking progress.
                </p>
                <p>
                  After implementing pEntities Ticketing, every request is now recorded as a ticket and
                  remains visible until completion. This gives us a clear picture of our pending work, helps
                  us prioritize effectively, and prevents important actions from slipping through the cracks.
                </p>
                <p>
                  The centralization of requests has also improved communication between departments because
                  all related information is available in one place, reducing unnecessary email exchanges and
                  accelerating resolution times.
                </p>
                <p>
                  Additionally, pEntities enables us to extract invoice information and, through integration
                  with Power Automate, fully automate approval workflows and accounting registration
                  processes. This has significantly reduced manual effort and minimized human error.
                </p>
                <p>
                  The Legal AI Agents have also helped us monitor critical dates and deadlines by proactively
                  reminding us about important obligations and upcoming actions.
                </p>
                <p>
                  Overall, pEntities Ticketing has helped us save time, improve organization, and increase
                  operational efficiency across our teams."
                </p>
                <cite> Stelios Georgiou IT Manager</cite>
              </blockquote>

              {/* The Challenge */}
              <h2>The Challenge</h2>
              <p>
                Like many organizations, the company relied heavily on email for internal requests and
                cross-department communication.
              </p>
              <p>
                While email is excellent for communication, it is not designed to operate as a structured
                work management platform.
              </p>
              <p>As the number of requests increased, several challenges emerged:</p>
              <ul>
                <li>Important requests became buried in crowded inboxes.</li>
                <li>There was no centralized visibility of pending work.</li>
                <li>Managers could not easily monitor workload or progress.</li>
                <li>Follow-ups depended on individual memory.</li>
                <li>Cross-department collaboration required long email chains.</li>
                <li>Service level commitments were difficult to track.</li>
                <li>Reporting and historical analysis were nearly impossible.</li>
              </ul>
              <p>
                The organization needed a solution that could centralize requests, provide accountability,
                and automate routine processes.
              </p>

              {/* The Solution */}
              <h2>The Solution: pEntities Ticketing</h2>
              <p>
                pEntities Ticketing provides a complete enterprise-grade issue and service management
                platform directly inside Microsoft Teams. Instead of scattered emails, every request becomes
                a structured ticket with ownership, status tracking, collaboration tools, notifications, and
                automation capabilities.
              </p>
              <p>
                The result is a single source of truth for all operational, IT, HR, Finance, Legal, and
                business service requests.
              </p>

              {/* How it works */}
              <h2>How pEntities Ticketing Works</h2>

              <h3>1. Centralized Request Management</h3>
              <p>Every request submitted by employees is converted into a managed ticket.</p>
              <p>Each ticket contains:</p>
              <ul>
                <li>Request details</li>
                <li>Priority</li>
                <li>Status</li>
                <li>Reporter</li>
                <li>Assignee</li>
                <li>Comments</li>
                <li>Attachments</li>
                <li>Activity history</li>
                <li>SLA targets</li>
              </ul>
              <p>
                This ensures that no request is forgotten, lost, or hidden inside an inbox. Every item
                remains visible until completion.
              </p>
              <figure className="bioland-figure">
                <img loading="lazy" decoding="async" src={ticket1} alt="Create Ticket Form" />
                <figcaption>Admin Module Tickets list </figcaption>
              </figure>

              <h3>2. Full Visibility Through Multiple Views</h3>
              <p>pEntities Ticketing offers several ways to monitor work depending on the user role.</p>
              <h4>Kanban Board</h4>
              <p>Visual board with statuses such as:</p>
              <ul>
                <li>Open</li>
                <li>In Progress</li>
                <li>In Review</li>
                <li>On Hold</li>
                <li>Resolved</li>
                <li>Closed</li>
              </ul>
              <p>
                Teams can drag and drop tickets between stages and instantly understand the current workload.
              </p>
              <figure className="bioland-figure">
                <img loading="lazy" decoding="async" src={ticket2} alt="Kanban Board" />
                <figcaption>Kanban Board</figcaption>
              </figure>
              <h4>List View</h4>
              <p>For users who prefer structured data, the List View provides:</p>
              <ul>
                <li>Sorting</li>
                <li>Filtering</li>
                <li>Search capabilities</li>
                <li>Bulk updates</li>
              </ul>
              <p>Ideal for service desk managers and operational teams.</p>
              <figure className="bioland-figure">
                <img loading="lazy" decoding="async" src={ticket3} alt="Ticket List View" />
                <figcaption>Admin Module Ticket Details</figcaption>
              </figure>
              <h4>Workload View</h4>
              <p>
                Provides a real-time overview of ticket distribution across agents, helping managers balance
                workload and identify bottlenecks early.
              </p>
              <figure className="bioland-figure">
                <img loading="lazy" decoding="async" src={ticket4} alt="Workload Dashboard" />
                <figcaption>Workload Dashboard</figcaption>
              </figure>

              <h3>3. Real-Time Collaboration</h3>
              <p>Instead of lengthy email threads, all communication happens directly inside the ticket.</p>
              <p>Users can:</p>
              <ul>
                <li>Add comments</li>
                <li>Mention colleagues</li>
                <li>Upload files</li>
                <li>Track decisions</li>
                <li>Review complete activity history</li>
              </ul>
              <p>
                Every ticket becomes a complete record of the work performed, improving transparency and
                accountability.
              </p>
              <figure className="bioland-figure">
                <img loading="lazy" decoding="async" src={ticket5} alt="Ticket Reporting & Activity Feed" />
                <figcaption>Ticket Reporting &amp; Activity Feed</figcaption>
              </figure>

              <h3>4. Intelligent Notifications</h3>
              <p>Whenever important actions occur, pEntities automatically notifies the relevant stakeholders.</p>
              <p>Examples include:</p>
              <ul>
                <li>New assignment</li>
                <li>Status changes</li>
                <li>New comments</li>
                <li>Escalations</li>
                <li>Approvals</li>
              </ul>
              <p>Notifications are delivered through:</p>
              <ul>
                <li>Microsoft Teams Adaptive Cards</li>
                <li>Email notifications</li>
              </ul>
              <p>This ensures everyone remains informed without constantly checking the application.</p>
              <figure className="bioland-figure">
                <img loading="lazy" decoding="async" src={ticket6} alt="Teams Notification Card" />
                <figcaption>Tickets Agent Teams Notification Card</figcaption>
              </figure>

              <h3>5. SLA Monitoring and Escalation</h3>
              <p>One of the most valuable capabilities for service-oriented teams is SLA management.</p>
              <p>pEntities continuously monitors:</p>
              <ul>
                <li>Response deadlines</li>
                <li>Resolution deadlines</li>
                <li>Ticket aging</li>
                <li>Escalation rules</li>
              </ul>
              <p>When a deadline is missed, the system can automatically:</p>
              <ul>
                <li>Escalate severity</li>
                <li>Notify managers</li>
                <li>Alert assignees</li>
                <li>Create audit records</li>
              </ul>
              <p>This helps organizations maintain service quality and accountability.</p>
              <figure className="bioland-figure">
                <img loading="lazy" decoding="async" src={ticket7} alt="SLA Dashboard" />
                <figcaption>Tickets Agent SLA Dashboard</figcaption>
              </figure>

              <h3>6. Automation with Power Automate</h3>
              <p>The customer particularly highlighted the value of automation around invoice processing.</p>
              <p>Using pEntities Document Intelligence and Power Automate:</p>
              <ul>
                <li>Invoice information is extracted automatically.</li>
                <li>Relevant metadata is identified.</li>
                <li>Approval workflows are triggered.</li>
                <li>Approvers receive notifications.</li>
                <li>Approved invoices are registered in accounting systems.</li>
              </ul>
              <p>
                This eliminates manual data entry and significantly accelerates financial operations.
              </p>

              <h3>7. AI-Powered Operational Assistance</h3>
              <p>
                Beyond ticket management, pEntities includes AI-powered capabilities that help organizations
                automate complex tasks.
              </p>
              <p>The customer's Legal team uses AI Agents to:</p>
              <ul>
                <li>Detect critical deadlines</li>
                <li>Search legal documents</li>
                <li>Monitor obligations</li>
                <li>Send reminders</li>
                <li>Reduce compliance risks</li>
              </ul>
              <p>
                Because these agents are integrated with workflows and business processes, they can
                proactively assist users instead of simply responding to questions.
              </p>

              {/* Business Impact */}
              <h2>Business Impact</h2>
              <p>
                After implementing pEntities Ticketing, the organization achieved measurable operational
                improvements:
              </p>
              <div className="bioland-impact-grid">
                <div className="bioland-impact-card">
                  <h4>Increased Visibility</h4>
                  <p>Every request is tracked from creation to closure.</p>
                </div>
                <div className="bioland-impact-card">
                  <h4>Faster Resolution Times</h4>
                  <p>Tickets are prioritized, assigned, and monitored more effectively.</p>
                </div>
                <div className="bioland-impact-card">
                  <h4>Improved Cross-Department Collaboration</h4>
                  <p>All information is centralized and accessible.</p>
                </div>
                <div className="bioland-impact-card">
                  <h4>Reduced Email Dependency</h4>
                  <p>Teams spend less time managing email chains.</p>
                </div>
                <div className="bioland-impact-card">
                  <h4>Process Automation</h4>
                  <p>Manual approval and processing activities are significantly reduced.</p>
                </div>
                <div className="bioland-impact-card">
                  <h4>Better Compliance</h4>
                  <p>AI-powered deadline management helps ensure important obligations are never missed.</p>
                </div>
                <div className="bioland-impact-card">
                  <h4>Greater Productivity</h4>
                  <p>Employees focus on completing work rather than searching for information.</p>
                </div>
              </div>

              {/* Conclusion */}
              <h2>Conclusion</h2>
              <p>
                What started as a need to prevent lost emails evolved into a broader digital transformation
                initiative.
              </p>
              <p>
                By implementing pEntities Ticketing, the organization established a structured, transparent,
                and automated operational model that connects people, processes, automation, and AI within
                Microsoft Teams.
              </p>
              <p className="bioland-result">The result is simple:</p>
              <p className="bioland-result-highlight">
                Fewer missed requests. Faster execution. Better visibility. Smarter operations.
              </p>

              <div className="bioland-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-proof-btn"
                >
                  Schedule a Demo
                </a>
                <a href="/solutions/ticketing" className="cta-proof-btn">
                  Explore pEntities Ticketing
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Lightbox */}
        {this.state.lightboxSrc && (
          <div className="bioland-lightbox" onClick={this.closeLightbox} role="dialog" aria-modal="true">
            <button className="bioland-lightbox-close" onClick={this.closeLightbox} aria-label="Close">✕</button>
            <img loading="lazy" decoding="async"
              className="bioland-lightbox-img"
              src={this.state.lightboxSrc}
              alt="Enlarged screenshot"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default Bioland;
